import { Link, useRoute } from 'wouter'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useBriefing } from '../lib/data-service'
import {
  ArrowLeft,
  Newspaper,
  BookOpen,
  Calendar,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Percent,
  Store,
  Download,
  Loader2,
} from 'lucide-react'

function parseDate(dateStr: string): Date {
  const ymd = dateStr.slice(0, 10)
  return new Date(ymd + 'T12:00:00')
}

function formatDateRange(start: string, end: string): string {
  const s = parseDate(start)
  const e = parseDate(end)
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' }
  if (start.slice(0, 10) === end.slice(0, 10)) {
    return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' })
  }
  const sStr = s.toLocaleDateString('en-US', opts)
  const eStr = e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })
  return `${sStr} \u2013 ${eStr}`
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val)
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function MetadataBar({ metadata, type }: { metadata: Record<string, any>; type: string }) {
  const m = metadata
  if (type === 'weekly') {
    const weekRevenueDtc = toNumber(m.week_revenue_dtc)
    const weekUnitsDtc = toNumber(m.week_units_dtc)
    const wowRevenueChange = toNumber(m.wow_revenue_change)
    const retailSellinTotal = toNumber(m.retail_sellin_total)
    const retailMarginBlended = toNumber(m.retail_margin_blended)

    const cards = [
      weekRevenueDtc !== null && {
        label: 'DTC Revenue',
        value: formatCurrency(weekRevenueDtc),
        icon: DollarSign,
        color: 'text-green-600 bg-green-100',
      },
      weekUnitsDtc !== null && {
        label: 'DTC Units',
        value: weekUnitsDtc.toLocaleString(),
        icon: ShoppingCart,
        color: 'text-blue-600 bg-blue-100',
      },
      wowRevenueChange !== null && {
        label: 'WoW Change',
        value: `${wowRevenueChange >= 0 ? '+' : ''}${wowRevenueChange.toFixed(1)}%`,
        icon: TrendingUp,
        color: wowRevenueChange >= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100',
      },
      retailSellinTotal !== null && {
        label: 'Retail Sell-in',
        value: formatCurrency(retailSellinTotal),
        icon: Store,
        color: 'text-indigo-600 bg-indigo-100',
      },
      retailMarginBlended !== null && {
        label: 'Blended Margin',
        value: `${retailMarginBlended.toFixed(1)}%`,
        icon: Percent,
        color: 'text-purple-600 bg-purple-100',
      },
    ].filter(Boolean) as Array<{ label: string; value: string; icon: any; color: string }>

    if (cards.length === 0) return null

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {cards.map(card => {
          const Icon = card.icon
          return (
            <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className={`p-1.5 rounded-lg ${card.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs text-gray-500">{card.label}</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{card.value}</div>
            </div>
          )
        })}
      </div>
    )
  }

  if (type === 'daily') {
    const shopifyRevenue = toNumber(m.shopify_revenue)
    const shopifyOrders = toNumber(m.shopify_orders)
    const shopifyUnits = toNumber(m.shopify_units)
    const shopifyAov = toNumber(m.shopify_aov)
    const trendDodRevenue = toNumber(m.trend_dod_revenue)

    const cards = [
      shopifyRevenue !== null && {
        label: 'Revenue',
        value: formatCurrency(shopifyRevenue),
        icon: DollarSign,
        color: 'text-green-600 bg-green-100',
      },
      shopifyOrders !== null && {
        label: 'Orders',
        value: shopifyOrders.toLocaleString(),
        icon: ShoppingCart,
        color: 'text-blue-600 bg-blue-100',
      },
      shopifyUnits !== null && {
        label: 'Units',
        value: shopifyUnits.toLocaleString(),
        icon: ShoppingCart,
        color: 'text-indigo-600 bg-indigo-100',
      },
      shopifyAov !== null && {
        label: 'AOV',
        value: formatCurrency(shopifyAov),
        icon: DollarSign,
        color: 'text-purple-600 bg-purple-100',
      },
      trendDodRevenue !== null && {
        label: 'DoD Revenue',
        value: `${trendDodRevenue >= 0 ? '+' : ''}${trendDodRevenue.toFixed(1)}%`,
        icon: TrendingUp,
        color: trendDodRevenue >= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100',
      },
    ].filter(Boolean) as Array<{ label: string; value: string; icon: any; color: string }>

    if (cards.length === 0) return null

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {cards.map(card => {
          const Icon = card.icon
          return (
            <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className={`p-1.5 rounded-lg ${card.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs text-gray-500">{card.label}</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{card.value}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

export default function BriefingDetail() {
  const [, params] = useRoute('/intelligence/:id')
  const id = params?.id || null
  const { data: briefing, isLoading, error } = useBriefing(id)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error || !briefing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-lg font-medium text-gray-900 mb-2">Briefing not found</p>
        <Link href="/intelligence" className="text-blue-600 hover:text-blue-700 text-sm">
          &larr; Back to Intelligence Hub
        </Link>
      </div>
    )
  }

  const isDaily = briefing.briefing_type === 'daily'

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-8 py-6">
        {/* Back navigation */}
        <Link
          href="/intelligence"
          className="inline-flex items-center space-x-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Intelligence Hub</span>
        </Link>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className={`p-2 rounded-xl ${isDaily ? 'bg-blue-100' : 'bg-purple-100'}`}>
              {isDaily ? (
                <Newspaper className={`w-5 h-5 text-blue-600`} />
              ) : (
                <BookOpen className={`w-5 h-5 text-purple-600`} />
              )}
            </div>
            <span
              className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                isDaily ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
              }`}
            >
              {isDaily ? 'Daily Briefing' : 'Weekly Digest'}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {formatDateRange(briefing.date_range_start, briefing.date_range_end)}
          </h1>

          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1.5">
              <Calendar className="w-4 h-4" />
              <span>Generated {formatTimestamp(briefing.generated_at)}</span>
            </div>
            <button
              disabled
              className="flex items-center space-x-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-gray-400 cursor-not-allowed"
              title="PDF export coming soon"
            >
              <Download className="w-3.5 h-3.5" />
              <span>PDF</span>
            </button>
          </div>
        </div>

        {/* Metadata bar */}
        {briefing.metadata && Object.keys(briefing.metadata).length > 0 && (
          <MetadataBar metadata={briefing.metadata} type={briefing.briefing_type} />
        )}

        {/* Markdown content */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="prose prose-gray max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-h1:text-2xl prose-h1:border-b prose-h1:border-gray-200 prose-h1:pb-3
            prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
            prose-h3:text-lg
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-strong:text-gray-900
            prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
            prose-ul:text-gray-700 prose-ol:text-gray-700
            prose-li:marker:text-gray-400
            prose-table:border-collapse prose-table:w-full
            prose-th:bg-gray-50 prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:text-sm prose-th:font-semibold prose-th:text-gray-700 prose-th:border prose-th:border-gray-200
            prose-td:px-4 prose-td:py-2 prose-td:text-sm prose-td:text-gray-600 prose-td:border prose-td:border-gray-200
            prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:text-gray-800
            prose-pre:bg-gray-900 prose-pre:rounded-xl
            prose-hr:border-gray-200
            prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:rounded-r-lg
          ">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {briefing.markdown_content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
}
